'use client';

import type { ReactElement } from 'react';

import { eventPlayerHref } from '../../libs/eventPlayers';
import { useEventFollows } from './EventFollowProvider';
import { useTranslations } from 'next-intl';
import { Link } from '@/components/ui/link';
import { Badge } from '@/components/ui/badge';
import { Numeric } from '@/components/ui/numeric';
import { useOutcomeLabel } from '@/components/match/matchOutcomeLabel';
import {
  MATCH_CARD_NAME_LINK,
  MatchResultCard,
  type MatchResultSide,
} from '@/components/match/MatchResultCard';
import { cn, formatRating, formatScoreChange } from '@/lib/utils';
import type { MatchRowVM, MatchSideVM } from '../../libs/eventMatchesView';
import { NAME_WRAP } from '../../libs/nameBreaks';
import { matchCardStatus, sideScoresLabel } from '../../libs/matchCardStatus';
import { NameText } from './NameText';

/** A public match row: maps one `MatchRowVM` onto the shared match card. */
export function EventMatchRow({
  row,
  showRatings,
  locale,
  eventId,
  slug,
}: {
  row: MatchRowVM;
  eventId?: string;
  slug?: string;
  showRatings: boolean;
  locale: string;
}): ReactElement {
  const t = useTranslations('Event.matches');
  const tMatches = useTranslations('Matches');
  const tFollow = useTranslations('Event.follow');
  const outcomeLabel = useOutcomeLabel();
  const follows = useEventFollows();
  const showRating = showRatings && !row.hideRating;
  const numbers = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });

  const outcomeWord = row.state === 'winnerless' ? outcomeLabel(row.outcome, row.isWalkover) : null;
  const { statusWord, status } = matchCardStatus({
    state: row.state,
    outcomeWord,
    retired: !!row.retired,
    words: {
      live: t('statusLive'),
      upcoming: t('statusUpcoming'),
      finished: t('statusFinished'),
      retiredShort: tMatches('retiredShort'),
    },
  });

  // Upcoming and winnerless outcomes have no played-score presentation.
  const hasScores =
    row.state !== 'upcoming' &&
    row.state !== 'winnerless' &&
    (row.games.length > 0 || row.gamesWon !== null);
  const showDelta =
    row.state === 'finished' &&
    !row.isWalkover &&
    (row.outcome == null || row.outcome === 'played' || row.outcome === 'retired');
  const decided = row.state === 'finished' || row.state === 'winnerless';

  const mapSide = (side: MatchSideVM, p: 0 | 1): MatchResultSide => {
    const ratingText =
      side.rating != null && Number.isFinite(side.rating)
        ? numbers.format(Number(formatRating(side.rating)))
        : null;
    const delta =
      showDelta && side.ratingDelta != null && Number.isFinite(side.ratingDelta)
        ? Number(formatScoreChange(side.ratingDelta))
        : null;
    const deltaText =
      delta === null
        ? null
        : `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${numbers.format(Math.abs(delta))}`;
    const lost = decided && row.sides[p === 0 ? 1 : 0].won;
    const href =
      eventId && slug && side.playerId
        ? `/${locale}${eventPlayerHref(eventId, slug, side.playerId, row.divisionId ?? undefined)}`
        : side.username
          ? `/user/${side.username}`
          : undefined;
    const you =
      side.playerId && side.playerId === follows.viewerPlayerId ? tFollow('you') : undefined;

    // `Link` brings its own `font-medium text-foreground`; the name carries the winner and loser
    // treatment itself so those win over it.
    const nameClass = cn(
      MATCH_CARD_NAME_LINK,
      NAME_WRAP,
      side.won && 'font-bold',
      lost && 'font-normal text-muted',
    );
    const name =
      side.name === '' ? (
        <span className="text-muted">{tMatches('tbd')}</span>
      ) : href ? (
        <Link href={href} prefetch={false} className={nameClass}>
          <NameText name={side.name} />
        </Link>
      ) : (
        <span className={NAME_WRAP}>
          <NameText name={side.name} />
        </span>
      );

    const base: MatchResultSide = {
      name: (
        <>
          {name}
          {you && <Badge variant="outline">{you}</Badge>}
        </>
      ),
      roster: side.roster ?? undefined,
      rating:
        showRating && ratingText !== null ? (
          <span
            data-rating-meta
            className="inline-flex flex-wrap items-baseline gap-x-1.5 text-[0.6875rem] whitespace-nowrap"
          >
            <Numeric className="text-muted" aria-label={t('ratingValue', { value: ratingText })}>
              {ratingText}
            </Numeric>
            {deltaText !== null && (
              <Numeric
                aria-label={t('ratingChange', { value: deltaText })}
                className={cn(
                  delta! > 0
                    ? 'text-success-emphasis'
                    : delta! < 0
                      ? 'text-danger-emphasis'
                      : 'text-muted',
                )}
              >
                {deltaText}
              </Numeric>
            )}
          </span>
        ) : undefined,
      won: side.won,
      lost,
      gamesWon: hasScores ? (row.gamesWon?.[p === 0 ? 'p1' : 'p2'] ?? null) : null,
      games: hasScores
        ? row.games.map((g) => {
            const mine = p === 0 ? g.p1 : g.p2;
            const theirs = p === 0 ? g.p2 : g.p1;
            return { score: mine, won: !g.partial && mine > theirs };
          })
        : [],
    };
    return { ...base, scoresLabel: sideScoresLabel((v) => t('sideScores', v), base) };
  };

  const [a, b] = row.sides;
  const action =
    row.state !== 'upcoming' && row.hasScorecard && row.matchId
      ? ({
          kind: 'scorecard',
          href: `/match/scorecard/${row.matchId}`,
          label: [t('viewScorecard'), a.name || tMatches('tbd'), b.name || tMatches('tbd')].join(
            ' · ',
          ),
        } as const)
      : undefined;

  return (
    <MatchResultCard
      id={row.id}
      state={row.state}
      statusWord={statusWord}
      winnerWord={t('winner')}
      header={{
        code: row.code ?? undefined,
        court: row.courtName ?? row.court,
        division: row.divisionName || undefined,
        status,
      }}
      sides={[mapSide(a, 0), mapSide(b, 1)]}
      action={action}
    />
  );
}
